import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProfileRequest, updateProfileRequest, clearError } from '../../redux/actions/authActions';
import { SafetyOutlined, UserOutlined, EditOutlined, CheckCircleOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import ChangePassword from './ChangePassword';
import './Auth.css';

function UserProfile() {
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    // Fetch user profile on component mount
    dispatch(getProfileRequest());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    // Update form data when user data is loaded
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      dispatch(updateProfileRequest(formData));
      setIsEditing(false);
    } else {
      setValidationErrors(newErrors);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
    }
    setValidationErrors({});
    setIsEditing(false);
    dispatch(clearError());
  };

  if (loading && !user) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (showChangePassword) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="page-header">
            <div className="page-header-title">
              <div className="page-header-icon"><SafetyOutlined /></div>
              <div className="page-header-text">
                <h1>Change Password</h1>
                <p>Update your account password</p>
              </div>
            </div>
            <div className="page-header-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowChangePassword(false)}
              >
                ← Back to Profile
              </button>
            </div>
          </div>
          <ChangePassword />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><UserOutlined /></div>
            <div className="page-header-text">
              <h1>User Profile</h1>
              <p>Manage your account information and settings</p>
            </div>
          </div>
          <div className="page-header-actions">
            {!isEditing && (
              <button
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                <EditOutlined /> Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Card */}
        <div className="form-container">

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <h3 className="form-section-title">Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={validationErrors.name ? 'form-input error' : 'form-input'}
                    disabled={loading || !isEditing}
                    placeholder="Enter your full name"
                  />
                  {validationErrors.name && (
                    <span className="form-error">{validationErrors.name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={validationErrors.email ? 'form-input error' : 'form-input'}
                    disabled={loading || !isEditing}
                    placeholder="Enter your email address"
                  />
                  {validationErrors.email && (
                    <span className="form-error">{validationErrors.email}</span>
                  )}
                </div>
              </div>
            </div>

            {user && (
              <div className="form-section">
                <h3 className="form-section-title">Account Information</h3>
                <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  <div className="stat-card">
                    <div className="stat-icon">
                      {user.email_verified ? <CheckCircleOutlined /> : <WarningOutlined />}
                    </div>
                    <div className="stat-label">Email Status</div>
                    <span className={`badge ${user.email_verified ? 'badge-success' : 'badge-warning'}`}>
                      {user.email_verified ? 'Verified' : 'Not Verified'}
                    </span>
                  </div>
                  {user.created_at && (
                    <div className="stat-card">
                      <div className="stat-icon"><CalendarOutlined /></div>
                      <div className="stat-label">Member Since</div>
                      <div className="stat-value" style={{ fontSize: '1rem' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isEditing ? (
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : '💾 Update Profile'}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="form-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowChangePassword(true)}
                >
                  <SafetyOutlined /> Change Password
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;